import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api, { apiErrorMessage } from "../../lib/api";
import { Chip, primaryBtn, secondaryBtn } from "./PortalShell";
import {
  STATES, dayText, hoursText, sessionState, standing, startingMarks, summaryLine,
} from "../../lib/simpleAttendance";

/**
 * Attendance on a Curs FPED cohort (13.10.2026): one mark per participant per
 * session, and how much of the 80% each has. The trainer's page and the
 * team's tab show the same thing — `base` is "/portal" or "/admin/portal".
 */
export default function SimpleAttendance({ base, groupId, onChanged }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      setData((await api.get(`${base}/groups/${groupId}/simple-attendance`)).data);
      setError("");
    } catch (err) {
      setError(apiErrorMessage(err, "Nu am putut încărca prezența."));
    }
  }, [base, groupId]);

  useEffect(() => { load(); }, [load]);

  if (error) return <p className="text-sm text-[#a33a32]" role="alert">{error}</p>;
  if (!data) return <div className="py-10 text-center text-[#6B7280]"><Loader2 className="mx-auto animate-spin" /></div>;

  const saved = (fresh) => { setData(fresh); setEditing(null); onChanged?.(); };

  return (
    <div data-testid="simple-attendance">
      <p className="text-sm text-[#6B7280]" data-testid="simple-attendance-summary">{summaryLine(data)}</p>

      {data.participants.length === 0 && (
        <p className="mt-3 rounded-2xl border border-[#E5E7EB] bg-white px-4 py-3 text-sm text-[#6B7280]">
          Grupa nu are încă cursanți în portal. Prezența se poate marca după ce intră primii.
        </p>
      )}

      <ul className="mt-4 grid gap-2">
        {data.sessions.map((s) => {
          const state = sessionState(s);
          const open = editing === s.key;
          return (
            <li key={s.key} className="rounded-2xl border border-[#E5E7EB] bg-white" data-testid={`simple-attendance-session-${s.number}`}>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-[#373A45]">
                    Sesiunea {s.number} <span className="font-normal text-[#6B7280]">· {dayText(s.date)} · {s.start_time}–{s.end_time}</span>
                  </p>
                </div>
                <Chip tone={state.tone}>{state.text}</Chip>
                {!open && (
                  <button type="button" className={secondaryBtn} disabled={!s.started || data.participants.length === 0}
                    title={s.started ? "" : "Prezența se marchează după ce începe sesiunea."}
                    onClick={() => setEditing(s.key)} data-testid={`simple-attendance-open-${s.number}`}>
                    {s.marked ? "Modifică" : "Marchează"}
                  </button>
                )}
              </div>
              {open && (
                <SessionEditor base={base} groupId={groupId} session={s} participants={data.participants}
                  onSaved={saved} onCancel={() => setEditing(null)} />
              )}
            </li>
          );
        })}
      </ul>

      {data.participants.length > 0 && data.sessions.length > 0 && (
        <div className="mt-6 overflow-x-auto rounded-2xl border border-[#E5E7EB] bg-white">
          <table className="w-full min-w-[28rem] border-collapse text-sm" data-testid="simple-attendance-people">
            <thead>
              <tr className="border-b border-[#E5E7EB] text-left text-xs text-[#6B7280]">
                <th scope="col" className="px-4 py-2.5 font-semibold">Cursant</th>
                <th scope="col" className="px-3 py-2.5 font-semibold">Ore la sesiuni</th>
                <th scope="col" className="px-3 py-2.5 font-semibold"><span className="sr-only">Starea</span></th>
              </tr>
            </thead>
            <tbody>
              {data.participants.map((p) => {
                const look = standing(p, data.complete);
                return (
                  <tr key={p.enrollment_id} className="border-b border-[#F0F1F3] last:border-0">
                    <td className="clarity-mask px-4 py-3 font-semibold text-[#373A45]">{p.name}</td>
                    <td className="px-3 py-3">
                      <div className="w-44">
                        <div className="text-xs tabular-nums text-[#373A45]">
                          {hoursText(p.attended_minutes)} din {hoursText(data.total_minutes)} · {p.percent}%
                        </div>
                        <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-[#E5E7EB]" aria-hidden="true">
                          <div className={`h-full rounded-full ${p.meets ? "bg-[#519872]" : "bg-[#965EA7]"}`}
                            style={{ width: `${Math.min(100, p.percent)}%` }} />
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3">{look && <Chip tone={look.tone}>{look.text}</Chip>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="mt-2 text-xs text-[#6B7280]">
        Contractul cere prezența la cel puțin {data.threshold_percent}% din durata sesiunilor. „Recuperat” se pune
        când cursantul a lipsit, dar a recuperat sesiunea cum ați stabilit; se socotește ca prezență.
      </p>
    </div>
  );
}

function SessionEditor({ base, groupId, session, participants, onSaved, onCancel }) {
  const [marks, setMarks] = useState(() => startingMarks(session, participants));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`${base}/groups/${groupId}/simple-attendance/${encodeURIComponent(session.key)}`, { marks });
      toast.success(`Prezența la sesiunea ${session.number} e salvată.`);
      onSaved(r.data);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Prezența nu a putut fi salvată."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-t border-[#E5E7EB] px-4 py-4" data-testid={`simple-attendance-editor-${session.number}`}>
      {!session.marked && (
        <p className="mb-3 text-xs text-[#6B7280]">Toți apar ca prezenți: schimbă doar la cine a lipsit.</p>
      )}
      <ul className="grid gap-2">
        {participants.map((p) => (
          <li key={p.enrollment_id} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
            <span className="clarity-mask min-w-0 text-sm text-[#373A45]">{p.name}</span>
            <span className="inline-flex overflow-hidden rounded-full border border-[#E5E7EB]" role="radiogroup"
              aria-label={`Prezența lui ${p.name}`}>
              {STATES.map((st) => {
                const on = marks[p.enrollment_id] === st.value;
                return (
                  <button key={st.value} type="button" role="radio" aria-checked={on}
                    onClick={() => setMarks((m) => ({ ...m, [p.enrollment_id]: st.value }))}
                    className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                      on ? (st.value === "absent" ? "bg-[#c0483f] text-white" : st.value === "recovered"
                        ? "bg-[#3F8EBA] text-white" : "bg-[#519872] text-white")
                        : "bg-white text-[#6B7280] hover:text-[#373A45]"}`}
                    data-testid={`simple-attendance-mark-${st.value}`}>
                    {st.label}
                  </button>
                );
              })}
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-4 flex flex-wrap gap-2">
        <button type="button" className={primaryBtn} onClick={save} disabled={saving} data-testid="simple-attendance-save">
          {saving ? <Loader2 size={16} className="animate-spin" /> : null} Salvează prezența
        </button>
        <button type="button" className={secondaryBtn} onClick={onCancel} disabled={saving}>Renunță</button>
      </div>
    </div>
  );
}
